/**
 * The add/edit entry screen — the whole point of the project (DESIGN.md §12.5).
 *
 * Defaults do the work: today, you paid, the group's currency, an even split, and the set
 * of people you split with last time. On a phone that leaves one number to type and a
 * button to press.
 *
 * The live preview calls the same `/transactions/preview` endpoint the real write uses, so
 * the arithmetic on screen is the arithmetic that gets saved.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Modal } from "../../components/ui/modal";
import { Spinner } from "../../components/ui/spinner";
import { useBalances, useGroup, useGroupId } from "../../components/layout/AppShell";
import {
  ApiError,
  api,
  newIdempotencyKey,
  type Preview,
  type Transaction,
} from "../../lib/api";
import { formatSigned, parseAmount, toAmountInput, toneClass } from "../../lib/money";
import { enqueue, isNetworkFailure } from "../../lib/offline";
import { cn } from "../../lib/utils";
import { useCategories, useMembers } from "../ledger/useLedger";
import { AdjustmentForm } from "./AdjustmentForm";
import { LineItemEditor, type DraftLineItem } from "./LineItemEditor";

type Mode = "expense" | "payment";
type Split = "even" | "individual" | "percentage";

const LAST_PARTICIPANTS_KEY = (groupId: number) => `splitwell.participants.${groupId}`;

/** Matches `DUPLICATE_WINDOW_DAYS` in `services/transactions.py` — used only in the copy. */
const DUPLICATE_WINDOW_DAYS = 30;

function loadLastParticipants(groupId: number): number[] | null {
  try {
    const raw = localStorage.getItem(LAST_PARTICIPANTS_KEY(groupId));
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === "number") : null;
  } catch {
    return null;
  }
}

function today(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

function useDebounced<T>(value: T, delay = 220): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function EntryForm() {
  const groupId = useGroupId();
  const { tid } = useParams();
  const [searchParams] = useSearchParams();
  const editingId = tid ? Number(tid) : null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const group = useGroup(groupId);
  const members = useMembers(groupId);
  const categories = useCategories(groupId);
  const balances = useBalances(groupId);

  const currency = group.data?.base_currency ?? "USD";
  const myMemberId = group.data?.my_member_id ?? null;
  const activeMembers = members.data ?? [];

  const existing = useQuery({
    queryKey: ["transaction", groupId, editingId],
    queryFn: () => api.get<Transaction>(`/groups/${groupId}/transactions/${editingId}`),
    enabled: editingId !== null,
  });

  const [mode, setMode] = useState<Mode>("expense");
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [occurredOn, setOccurredOn] = useState(today());
  const [payerId, setPayerId] = useState<number | null>(null);
  const [split, setSplit] = useState<Split>("even");
  const [participants, setParticipants] = useState<number[]>([]);
  const [individual, setIndividual] = useState<Record<number, string>>({});
  const [percentages, setPercentages] = useState<Record<number, string>>({});
  const [groupCost, setGroupCost] = useState("");
  const [lineItems, setLineItems] = useState<DraftLineItem[]>([]);
  const [useLineItems, setUseLineItems] = useState(false);
  const [comment, setComment] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [entryCurrency, setEntryCurrency] = useState<string>(currency);
  const [fxRate, setFxRate] = useState("");
  const [fromId, setFromId] = useState<number | null>(null);
  const [toId, setToId] = useState<number | null>(null);
  const [showMore, setShowMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isQueued, setQueued] = useState(false);
  const idempotencyKey = useRef(newIdempotencyKey());
  const seeded = useRef(false);

  // Defaults, once the group and members have loaded. The Balances page links here
  // with the settle-up payment already filled in.
  useEffect(() => {
    if (seeded.current || activeMembers.length === 0 || editingId !== null) return;
    seeded.current = true;
    const prefillFrom = Number(searchParams.get("from")) || null;
    const prefillTo = Number(searchParams.get("to")) || null;
    const prefillAmount = Number(searchParams.get("amount")) || null;
    if (searchParams.get("type") === "payment") {
      setMode("payment");
      setName("Settling up");
    }
    if (prefillAmount) setAmount(toAmountInput(prefillAmount, currency));
    if (prefillTo) setToId(prefillTo);
    setPayerId(myMemberId ?? activeMembers[0]?.id ?? null);
    setFromId(prefillFrom ?? myMemberId ?? activeMembers[0]?.id ?? null);
    const remembered = loadLastParticipants(groupId);
    const valid = (remembered ?? []).filter((id) => activeMembers.some((m) => m.id === id));
    setParticipants(valid.length ? valid : activeMembers.map((member) => member.id));
    setEntryCurrency(currency);
  }, [activeMembers, currency, editingId, groupId, myMemberId]);

  // Load an existing entry when editing.
  useEffect(() => {
    const transaction = existing.data;
    if (!transaction || seeded.current) return;
    seeded.current = true;
    setMode(transaction.type === "payment" ? "payment" : "expense");
    setName(transaction.name);
    setAmount(toAmountInput(transaction.total_original_cents, transaction.currency_code));
    setOccurredOn(transaction.occurred_on);
    setPayerId(transaction.payer_member_id);
    setSplit((transaction.split as Split | null) ?? "even");
    setParticipants(transaction.participants.map((participant) => participant.member_id));
    setIndividual(
      Object.fromEntries(
        transaction.participants.map((participant) => [
          participant.member_id,
          toAmountInput(participant.individual_original_cents, transaction.currency_code),
        ]),
      ),
    );
    setPercentages(
      Object.fromEntries(
        transaction.participants
          .filter((participant) => participant.percentage_bp !== null)
          .map((participant) => [participant.member_id, String((participant.percentage_bp ?? 0) / 100)]),
      ),
    );
    setGroupCost(
      toAmountInput(transaction.group_cost_original_cents, transaction.currency_code) || "",
    );
    setUseLineItems(transaction.individual_source === "line_items");
    setLineItems(
      transaction.line_items.map((item) => ({
        key: `item-${item.id}`,
        name: item.name,
        amount: toAmountInput(item.amount_original_cents, transaction.currency_code),
        assignees: item.assignee_member_ids,
      })),
    );
    setComment(transaction.comment ?? "");
    setCategoryId(transaction.category_id);
    setEntryCurrency(transaction.currency_code);
    setFxRate(transaction.currency_code === currency ? "" : String(transaction.fx_rate_to_base));
    setFromId(transaction.from_member_id);
    setToId(transaction.to_member_id);
    setShowMore(true);
  }, [currency, existing.data]);

  const amountCents = parseAmount(amount, entryCurrency);

  const payload = useMemo(() => {
    if (!amountCents || amountCents <= 0) return null;
    const base: Record<string, unknown> = {
      type: mode,
      name: name.trim() || "Expense",
      occurred_on: occurredOn,
      total_original_cents: amountCents,
      comment: comment.trim() || null,
    };
    if (entryCurrency !== currency) {
      base.currency_code = entryCurrency;
      const rate = fxRate.trim();
      if (!rate) return null;
      base.fx_rate_to_base = rate;
    }
    if (mode === "payment") {
      if (!fromId || !toId || fromId === toId) return null;
      base.from_member_id = fromId;
      base.to_member_id = toId;
      base.name = name.trim() || "Payment";
      return base;
    }
    if (!payerId || participants.length === 0) return null;
    base.payer_member_id = payerId;
    base.split = split;
    base.category_id = categoryId;
    base.participants = participants.map((memberId) => ({
      member_id: memberId,
      individual_original_cents:
        split === "individual" && !useLineItems
          ? (parseAmount(individual[memberId] ?? "", entryCurrency) ?? 0)
          : 0,
      percentage_bp:
        split === "percentage"
          ? Math.round(Number(percentages[memberId] ?? 0) * 100) || 0
          : null,
      shares_group_cost: true,
    }));
    if (split === "individual") {
      base.group_cost_original_cents = useLineItems
        ? 0
        : (parseAmount(groupCost, entryCurrency) ?? 0);
      base.individual_source = useLineItems ? "line_items" : "manual";
      if (useLineItems) {
        base.line_items = lineItems
          .filter((item) => item.name.trim() && parseAmount(item.amount, entryCurrency))
          .map((item) => ({
            name: item.name.trim(),
            amount_original_cents: parseAmount(item.amount, entryCurrency) ?? 0,
            assignee_member_ids: item.assignees,
          }));
      }
    }
    return base;
  }, [
    amountCents,
    categoryId,
    comment,
    currency,
    entryCurrency,
    fromId,
    fxRate,
    groupCost,
    individual,
    lineItems,
    mode,
    name,
    occurredOn,
    participants,
    payerId,
    percentages,
    split,
    toId,
    useLineItems,
  ]);

  /**
   * Why the entry cannot be saved yet, in words. Without this the button simply refuses
   * to work and the person is left guessing which field is wrong — which is exactly what
   * happened with percentage splits.
   */
  const blockedReason = useMemo((): string | null => {
    if (!amount.trim()) return "Enter an amount.";
    if (!amountCents || amountCents <= 0) return "That amount is not a number.";
    if (entryCurrency !== currency && !fxRate.trim()) {
      return `Enter what 1 ${entryCurrency} is worth in ${currency}.`;
    }
    if (mode === "payment") {
      if (!fromId || !toId) return "Choose who paid and who they paid.";
      if (fromId === toId) return "A payment needs two different people.";
      return null;
    }
    if (participants.length === 0) return "Pick at least one person to split with.";
    if (split === "percentage") {
      const total = participants.reduce(
        (sum, id) => sum + (Number(percentages[id] ?? 0) || 0),
        0,
      );
      if (Math.abs(total - 100) > 0.001) {
        return `Percentages add up to ${total.toFixed(2)}%, not 100%.`;
      }
    }
    return null;
  }, [
    amount,
    amountCents,
    currency,
    entryCurrency,
    fromId,
    fxRate,
    mode,
    participants,
    percentages,
    split,
    toId,
  ]);

  const debouncedPayload = useDebounced(payload);

  const preview = useQuery({
    queryKey: ["preview", groupId, editingId, JSON.stringify(debouncedPayload)],
    queryFn: () =>
      api.post<Preview>(`/groups/${groupId}/transactions/preview`, {
        ...debouncedPayload,
        // Editing an entry: it is not a duplicate of itself.
        editing_transaction_id: editingId,
      }),
    enabled: debouncedPayload !== null,
    retry: false,
  });

  // "Did I already add this?" — same total, same month. Asked once, then it stays out of
  // the way: two identical rounds of drinks on one night are a real thing.
  const duplicates = preview.data?.possible_duplicates ?? [];
  const [duplicatesOk, setDuplicatesOk] = useState(false);
  const [askingDuplicate, setAskingDuplicate] = useState(false);

  const save = useMutation({
    mutationFn: async () => {
      if (!payload) throw new ApiError(400, { code: "incomplete", message: "Fill in the amount." });
      if (editingId !== null) {
        return api.patch<Transaction>(
          `/groups/${groupId}/transactions/${editingId}`,
          payload,
          { headers: { "If-Match": String(existing.data?.version ?? "") } },
        );
      }
      try {
        return await api.post<Transaction>(`/groups/${groupId}/transactions`, payload, {
          idempotencyKey: idempotencyKey.current,
        });
      } catch (cause) {
        // No signal: keep the entry locally under its idempotency key and replay it on
        // reconnect. The server dedupes, so a crossed request cannot double-charge.
        if (isNetworkFailure(cause)) {
          await enqueue(groupId, payload, idempotencyKey.current);
          setQueued(true);
          return null;
        }
        throw cause;
      }
    },
    onSuccess: (saved) => {
      if (mode === "expense") {
        localStorage.setItem(LAST_PARTICIPANTS_KEY(groupId), JSON.stringify(participants));
      }
      void queryClient.invalidateQueries({ queryKey: ["transactions", groupId] });
      if (saved) {
        // The drawer, and the next edit, read this one entry from its own cache entry:
        // without the saved copy there they showed the old comment, and the next save
        // sent the old version and was refused as a conflict.
        queryClient.setQueryData(["transaction", groupId, saved.id], saved);
      }
      void balances.refetch();
      if (saved === null) {
        // Queued offline: let the person read what happened before moving on.
        setTimeout(() => navigate(`/g/${groupId}`), 1800);
        return;
      }
      navigate(`/g/${groupId}`);
    },
    onError: (cause) => {
      setError(cause instanceof ApiError ? cause.message : "Could not save the entry.");
    },
  });

  const names = new Map(activeMembers.map((member) => [member.id, member.display_name]));
  const previewError = preview.error instanceof ApiError ? preview.error.message : null;

  const toggleParticipant = (memberId: number) =>
    setParticipants((current) =>
      current.includes(memberId)
        ? current.filter((id) => id !== memberId)
        : [...current, memberId],
    );

  if (members.isPending || (editingId !== null && existing.isPending)) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // An adjustment is edited by its lines, not recomputed from a payer and a split.
  if (existing.data?.type === "adjustment") {
    return <AdjustmentForm groupId={groupId} transaction={existing.data} />;
  }

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
      <Modal
        open={askingDuplicate}
        onOpenChange={setAskingDuplicate}
        title="Is this a duplicate?"
        description={`The same total is already on the ledger within ${DUPLICATE_WINDOW_DAYS} days.`}
        footer={
          <>
            <Button onClick={() => setAskingDuplicate(false)}>Go back</Button>
            <Button
              variant="primary"
              onClick={() => {
                setDuplicatesOk(true);
                setAskingDuplicate(false);
                save.mutate();
              }}
            >
              Add it anyway
            </Button>
          </>
        }
      >
        <ul className="divide-y divide-rule text-sm">
          {duplicates.map((candidate) => (
            <li key={candidate.id} className="flex justify-between gap-3 py-2">
              <span className="min-w-0">
                <span className="block truncate">{candidate.name}</span>
                <span className="text-xs text-muted">
                  {new Date(`${candidate.occurred_on}T00:00:00`).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                  {candidate.payer_member_id
                    ? ` · ${names.get(candidate.payer_member_id) ?? ""} paid`
                    : ""}
                </span>
              </span>
              <span className="num shrink-0">
                {formatSigned(candidate.total_base_cents, currency).replace("+", "")}
              </span>
            </li>
          ))}
        </ul>
      </Modal>

      <div className="mx-auto flex w-full max-w-xl shrink-0 items-center justify-between py-3">
        <h1 className="display text-sm text-muted">
          {editingId !== null ? "Edit entry" : "Add entry"}
        </h1>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="rounded-lg px-2.5 py-2 text-sm text-muted transition-colors hover:bg-ink/[0.04] hover:text-ink"
        >
          Cancel
        </button>
      </div>

      {/* Nothing on this form scrolls sideways, ever. One stubborn control should push
          its own row around, not the whole entry screen — see the note in AppShell for
          why this is `clip` rather than `hidden`. */}
      <div className="scroll-region min-h-0 flex-1 overflow-y-auto overflow-x-clip">
        <div className="mx-auto w-full min-w-0 max-w-xl pb-4">

        <div className="mb-3 flex gap-1">
          {(["expense", "payment"] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setMode(value)}
              aria-pressed={mode === value}
              className={cn("chip tap flex-1 justify-center", mode === value && "chip-on")}
            >
              {value === "expense" ? "Expense" : "Payment"}
            </button>
          ))}
        </div>

        {error ? (
          <p role="alert" className="mb-3 rounded border border-owing/40 bg-owing/10 px-3 py-2 text-sm">
            {error}
          </p>
        ) : null}

        {isQueued ? (
          <p className="mb-3 rounded border border-accent/40 bg-accent/5 px-3 py-2 text-sm">
            Saved on this device. It will go up as soon as you have signal.
          </p>
        ) : null}

        <label className="block text-2xs uppercase tracking-widest text-muted" htmlFor="amount">
          Amount ({entryCurrency})
        </label>
        <input
          id="amount"
          inputMode="decimal"
          autoFocus
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          placeholder="0.00"
          aria-label={`Amount in ${entryCurrency}`}
          className="num mb-4 w-full min-w-0 border-b-2 border-rule bg-transparent py-2 text-4xl outline-none transition-colors focus:border-accent focus:ring-0"
        />

        <label className="block text-2xs uppercase tracking-widest text-muted" htmlFor="entry-name">
          {mode === "expense" ? "What was it?" : "Note"}
        </label>
        <Input
          id="entry-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={mode === "expense" ? "Malatang, Costco, rent…" : "Venmo"}
          list="entry-name-suggestions"
          className="mb-4"
        />

        {mode === "payment" ? (
          <div className="mb-4 grid grid-cols-2 gap-3">
            <label className="text-2xs uppercase tracking-widest text-muted">
              From
              <select
                className="field mt-1"
                value={fromId ?? ""}
                onChange={(event) => setFromId(Number(event.target.value))}
              >
                {activeMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.display_name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-2xs uppercase tracking-widest text-muted">
              To
              <select
                className="field mt-1"
                value={toId ?? ""}
                onChange={(event) => setToId(Number(event.target.value))}
              >
                <option value="">Choose…</option>
                {activeMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.display_name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : (
          <>
            <div className="mb-1 flex items-baseline justify-between gap-3">
              <p className="text-2xs uppercase tracking-widest text-muted">Split between</p>
              <button
                type="button"
                className="text-xs text-accent underline underline-offset-2"
                onClick={() =>
                  setParticipants(
                    participants.length === activeMembers.length
                      ? []
                      : activeMembers.map((member) => member.id),
                  )
                }
              >
                {participants.length === activeMembers.length ? "Deselect all" : "Select all"}
              </button>
            </div>
            <div className="mb-4 flex flex-wrap gap-1.5">
              {activeMembers.map((member) => (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => toggleParticipant(member.id)}
                  aria-pressed={participants.includes(member.id)}
                  className={cn("chip tap", participants.includes(member.id) && "chip-on")}
                >
                  {member.display_name}
                  {member.id === myMemberId ? " (you)" : ""}
                </button>
              ))}
            </div>

            <div className="mb-4 flex gap-1">
              {(
                [
                  ["even", "Even"],
                  ["individual", "Individual"],
                  ["percentage", "%"],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setSplit(value)}
                  aria-pressed={split === value}
                  className={cn("chip tap flex-1 justify-center", split === value && "chip-on")}
                >
                  {label}
                </button>
              ))}
            </div>

            {split === "individual" ? (
              <section className="mb-4 rounded border border-rule p-3">
                <div className="mb-2 flex items-center justify-between">
                  <h2 className="text-2xs uppercase tracking-widest text-muted">
                    Individual amounts
                  </h2>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={useLineItems}
                      onChange={(event) => setUseLineItems(event.target.checked)}
                      className="h-4 w-4 accent-[rgb(var(--accent))]"
                    />
                    From line items
                  </label>
                </div>

                {useLineItems ? (
                  <LineItemEditor
                    items={lineItems}
                    onChange={setLineItems}
                    members={activeMembers.filter((member) => participants.includes(member.id))}
                    currency={entryCurrency}
                  />
                ) : (
                  <div className="flex flex-col gap-2">
                    {participants.map((memberId) => (
                      <label key={memberId} className="flex items-center gap-3 text-sm">
                        <span className="w-24 shrink-0 truncate sm:w-28">{names.get(memberId)}</span>
                        <Input
                          inputMode="decimal"
                          className="h-10"
                          placeholder="0.00"
                          aria-label={`${names.get(memberId)}'s individual amount`}
                          value={individual[memberId] ?? ""}
                          onChange={(event) =>
                            setIndividual((current) => ({
                              ...current,
                              [memberId]: event.target.value,
                            }))
                          }
                        />
                      </label>
                    ))}
                    <label className="flex items-center gap-3 text-sm">
                      <span className="w-24 shrink-0 text-muted sm:w-28">Group cost</span>
                      <Input
                        inputMode="decimal"
                        className="h-10"
                        placeholder="0.00"
                        aria-label="Shared group cost"
                        value={groupCost}
                        onChange={(event) => setGroupCost(event.target.value)}
                      />
                    </label>
                  </div>
                )}

                {preview.data ? (
                  <p className="mt-2 text-xs text-muted">
                    {/* The same derived number both ways: what is left over after the
                        items is tax and tip when positive, and a discount when the total
                        is lower than the items add up to. Labelling a negative figure
                        "tax + tip" reads as a bug. */}
                    {preview.data.derived_tax_tip_original_cents < 0
                      ? "Discount (derived) "
                      : "Tax + tip (derived) "}
                    <span className="num">
                      {formatSigned(preview.data.derived_tax_tip_original_cents, entryCurrency)}
                    </span>
                  </p>
                ) : null}
                {preview.data?.warnings.map((warning) => (
                  <p key={warning} className="mt-1 text-xs text-owing">
                    {warning}
                  </p>
                ))}
              </section>
            ) : null}

            {split === "percentage" ? (
              <section className="mb-4 rounded border border-rule p-3">
                <div className="flex flex-col gap-2">
                  {participants.map((memberId) => (
                    <label key={memberId} className="flex items-center gap-3 text-sm">
                      <span className="w-24 shrink-0 truncate sm:w-28">{names.get(memberId)}</span>
                      <Input
                        inputMode="decimal"
                        className="h-10"
                        placeholder="0.00"
                        aria-label={`${names.get(memberId)}'s percentage`}
                        value={percentages[memberId] ?? ""}
                        onChange={(event) =>
                          setPercentages((current) => ({
                            ...current,
                            [memberId]: event.target.value,
                          }))
                        }
                      />
                      <span className="text-muted">%</span>
                    </label>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  {(() => {
                    const remaining =
                      100 -
                      participants.reduce((sum, id) => sum + (Number(percentages[id] ?? 0) || 0), 0);
                    const done = Math.abs(remaining) < 0.001;
                    return (
                      <span
                        className={cn(
                          "rounded-full px-2 py-1 font-medium transition-colors",
                          done ? "bg-owed/12 text-owed" : "bg-owing/12 text-owing",
                        )}
                      >
                        {done ? "Adds up to 100%" : `${remaining.toFixed(2)}% left`}
                      </span>
                    );
                  })()}
                  <button
                    type="button"
                    className="text-accent underline underline-offset-2"
                    onClick={() => {
                      const used = participants.reduce(
                        (sum, id) => sum + (Number(percentages[id] ?? 0) || 0),
                        0,
                      );
                      const blanks = participants.filter((id) => !percentages[id]);
                      if (!blanks.length) return;
                      const each = Math.floor(((100 - used) / blanks.length) * 100) / 100;
                      const next = { ...percentages };
                      blanks.forEach((id, index) => {
                        next[id] = String(
                          index === blanks.length - 1
                            ? Math.round((100 - used - each * (blanks.length - 1)) * 100) / 100
                            : each,
                        );
                      });
                      setPercentages(next);
                    }}
                  >
                    Split the rest evenly
                  </button>
                </div>
              </section>
            ) : null}
          </>
        )}

        {/* The live preview: what each balance would move by, from the server's own maths. */}
        <div
          aria-live="polite"
          className="mb-4 min-h-[2.75rem] rounded border border-rule bg-raised px-3 py-2 text-sm"
        >
          {preview.isFetching ? (
            <span className="text-muted">Working it out…</span>
          ) : previewError ? (
            <span className="text-owing">{previewError}</span>
          ) : preview.data ? (
            <span className="flex flex-wrap gap-x-3 gap-y-1">
              {Object.entries(preview.data.ledger)
                .map(([memberId, delta]) => [Number(memberId), delta] as const)
                .sort((a, b) => b[1] - a[1])
                .map(([memberId, delta]) => (
                  <span key={memberId}>
                    <span className="text-muted">
                      {memberId === myMemberId ? "You" : (names.get(memberId) ?? "?")}{" "}
                    </span>
                    <span className={cn("num", toneClass(delta))}>
                      {formatSigned(delta, currency)}
                    </span>
                  </span>
                ))}
            </span>
          ) : (
            <span className="text-muted">Enter an amount to see the split.</span>
          )}
        </div>

        <button
          type="button"
          onClick={() => setShowMore((value) => !value)}
          aria-expanded={showMore}
          className="tap mb-2 text-sm text-accent underline underline-offset-2"
        >
          {showMore ? "Less" : "More"} options
        </button>

        {showMore ? (
          <section className="mb-4 flex min-w-0 flex-col gap-3 rounded-xl border border-rule p-3">
            <label className="block min-w-0 text-2xs uppercase tracking-widest text-muted">
              Date
              <Input
                type="date"
                className="mt-1 min-w-0"
                value={occurredOn}
                onChange={(event) => setOccurredOn(event.target.value)}
              />
            </label>

            {mode === "expense" ? (
              <label className="text-2xs uppercase tracking-widest text-muted">
                Paid by
                <select
                  className="field mt-1"
                  value={payerId ?? ""}
                  onChange={(event) => setPayerId(Number(event.target.value))}
                >
                  {activeMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.display_name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            {mode === "expense" ? (
              <label className="text-2xs uppercase tracking-widest text-muted">
                Category
                <select
                  className="field mt-1"
                  value={categoryId ?? ""}
                  onChange={(event) =>
                    setCategoryId(event.target.value ? Number(event.target.value) : null)
                  }
                >
                  <option value="">Automatic</option>
                  {(categories.data ?? []).map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            <div className="grid grid-cols-2 gap-3">
              <label className="text-2xs uppercase tracking-widest text-muted">
                Currency
                <Input
                  className="mt-1 uppercase"
                  maxLength={3}
                  value={entryCurrency}
                  onChange={(event) => setEntryCurrency(event.target.value.toUpperCase())}
                />
              </label>
              {entryCurrency !== currency ? (
                <label className="text-2xs uppercase tracking-widest text-muted">
                  1 {entryCurrency} = ? {currency}
                  <Input
                    inputMode="decimal"
                    className="mt-1"
                    placeholder="1.09"
                    value={fxRate}
                    onChange={(event) => setFxRate(event.target.value)}
                  />
                </label>
              ) : null}
            </div>

            <label className="text-2xs uppercase tracking-widest text-muted">
              Comment
              <Input
                className="mt-1"
                value={comment}
                onChange={(event) => setComment(event.target.value)}
              />
            </label>
          </section>
        ) : null}

        </div>
      </div>

      <div className="shrink-0 border-t border-rule bg-paper py-3">
        <div className="mx-auto w-full max-w-xl">
          {blockedReason ? (
            <p className="mb-2 text-center text-xs text-muted">{blockedReason}</p>
          ) : null}
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            disabled={!payload || Boolean(blockedReason) || save.isPending}
            onClick={() => {
              setError(null);
              if (duplicates.length > 0 && !duplicatesOk) {
                setAskingDuplicate(true);
                return;
              }
              save.mutate();
            }}
          >
            {save.isPending
              ? "Saving…"
              : editingId !== null
                ? "Save changes"
                : mode === "payment"
                  ? "Record payment"
                  : "Add entry"}
          </Button>
        </div>
      </div>
    </div>
  );
}
